import React, { useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import Breadcrums from "../../common/breadcrumbs";
import {
  globalLoader,
  handleServerValidations,
} from "../../../../utils/commonfunction";
import CustomError from "../../../../utils/customError";
import {
  subscriptionPlanType,
  SWAL_SETTINGS,
} from "../../../../utils/Constants";
import {
  SubscriptionPlanAdd,
  SubscriptionPlanList,
} from "../../services/subscriptionPlan.services";

const AddSubscriptionPlan = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const [planList, setPlanList] = useState([]);
  const [isLoadingMeta, setIsLoadingMeta] = useState(true);
  const breadcrumbs = [
    { title: t("link_dashboard"), url: "/admin/dashboard" },
    {
      title: t("Subscription Plan"),
      url: "/admin/subscription/subscription-plan/list/1",
    },
    { title: t("link_add"), url: "" },
  ];

  const validationSchema = Yup.object().shape({
    plan_group: Yup.string()
      .oneOf(["current", "other"], "Invalid plan group")
      .required("Plan group is required"),
    plan_name: Yup.string().trim().required("Plan name is required"),
    price: Yup.number().when("plan_group", {
      is: "current",
      then: () => Yup.number().typeError("Price must be a number").min(0),
      otherwise: () =>
        Yup.number()
          .typeError("Price must be a number")
          .required("Price is required")
          .min(0, "Price cannot be less than 0")
          .max(10000000, "Price cannot exceed 10000000"),
    }),
    description: Yup.string()
      .trim()
      .required("Description is required")
      .max(500, "Description cannot exceed 500 characters"),
  });

  useEffect(() => {
    const formData = new FormData();
    formData.append("page", 1);
    formData.append("per_page", 100);
    formData.append("sort", JSON.stringify({ order: -1, column: "createdAt" }));
    formData.append("search", JSON.stringify({}));
    formData.append("global_search", "");
    SubscriptionPlanList(formData)
      .then((data) => {
        setPlanList(data?.data?.list || []);
      })
      .catch(() => {
        setPlanList([]);
      })
      .finally(() => {
        setIsLoadingMeta(false);
      });
  }, []);

  const { currentPlanExists, otherPlanCount } = useMemo(() => {
    const list = planList || [];
    const currentCount = list.filter(
      (item) => item?.plan_group === "current" || Number(item?.price) === 0,
    ).length;
    const otherCount = list.length - currentCount;
    return {
      currentPlanExists: currentCount > 0,
      otherPlanCount: otherCount,
    };
  }, [planList]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      plan_group: "other",
      plan_name: "",
      price: "",
      description: "",
    },
    validateOnBlur: false,
    validationSchema: validationSchema,
    onSubmit: (values, { setSubmitting, resetForm }) => {
      globalLoader(true);
      setSubmitted(true);
      let formData = new FormData();
      formData.append("plan_group", values.plan_group);
      formData.append("plan_name", values.plan_name);
      formData.append(
        "price",
        values.plan_group === "current" ? 0 : values.price,
      );
      formData.append("description", values.description);

      SubscriptionPlanAdd(formData)
        .then((response) => {
          setSubmitting(false);
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message,
              ...SWAL_SETTINGS,
            });
            globalLoader(false);
            resetForm({ values: "" });
            navigate(`/admin/subscription/subscription-plan/list/1`);
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
            globalLoader(false);
            setSubmitted(false);
          }
        })
        .catch((error) => {
          console.log("error ====> ", error);
        });
    },
  });

  useEffect(() => {
    if (formik.values.plan_group === "current" && formik.values.price !== 0) {
      formik.setFieldValue("price", 0);
    }
  }, [formik.values.plan_group]);

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <form onSubmit={formik.handleSubmit}>
        <div className="row row-sm">
          <div className="col-lg-12 col-md-12 animation_fade">
            <div className="card custom-card">
              <div className="card-body">
                <div>
                  <h6 className="main-content-label mb-3">
                    {t("Add Subscription plan")}
                  </h6>
                </div>
                <div className="row row-sm">
                  <div className="col-md-6 text-center form-group">
                    <label htmlFor="plan_group" className="text-left d-flex">
                      {t("Plan group")}:
                      <span className="requirestar">*</span>{" "}
                    </label>
                    <div className=" select-down-arrow">
                      <select
                        name="plan_group"
                        id="plan_group"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.plan_group}
                        className="form-control"
                        disabled={isLoadingMeta}
                      >
                        <option value="other">Other Plan</option>
                        <option value="current" disabled={currentPlanExists}>
                          Current Plan
                        </option>
                      </select>
                    </div>
                    <span className="text-danger d-flex text-left">
                      <CustomError name="plan_group" form={formik} />
                    </span>
                  </div>

                  <div className="col-md-12 text-center form-group">
                    <label htmlFor="description" className="text-left d-flex">
                      {t("Description")}:
                      <span className="requirestar">*</span>{" "}
                    </label>
                    <textarea
                      name="description"
                      id="description"
                      rows={4}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.description}
                      className="form-control"
                      placeholder={t("Enter plan description")}
                    />
                    <span className="text-danger d-flex text-left">
                      <CustomError name="description" form={formik} />
                    </span>
                  </div>

                  <div className="col-md-6 text-center form-group">
                    <label htmlFor="plan_name" className="text-left d-flex">
                      {t("Plan name")}:
                      <span className="requirestar">*</span>{" "}
                    </label>
                    <div className=" select-down-arrow">
                      <select
                        name="plan_name"
                        id="plan_name"
                        type="text"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.plan_name}
                        className="form-control"
                        placeholder={t("Enter plan name")}
                      >
                        <option value={""}>Select</option>
                        {subscriptionPlanType
                          .filter((value) => !value?.isLegacy)
                          .map((value, i) => {
                          return (
                            <option key={i} value={value.value}>
                              {t(`${value.name}`)}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                    <span className="text-danger d-flex text-left">
                      <CustomError name="plan_name" form={formik} />
                    </span>
                  </div>

                  <div className="col-md-6 text-center form-group">
                    <label htmlFor="price" className="text-left d-flex">
                      {t("Plan price")}:
                      <span className="requirestar">*</span>{" "}
                    </label>
                    <input
                      name="price"
                      id="price"
                      type="text"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={
                        formik.values.plan_group === "current"
                          ? "0"
                          : formik.values.price
                      }
                      className="form-control"
                      placeholder={t("Enter plan price")}
                      disabled={formik.values.plan_group === "current"}
                    />
                    <span className="text-danger d-flex text-left">
                      <CustomError name="price" form={formik} />
                    </span>
                  </div>

                  {formik.values.plan_group === "other" && otherPlanCount >= 2 ? (
                    <div className="col-md-12">
                      <span className="text-danger d-flex text-left">
                        You can only create 2 Other Plans. Delete one to add a
                        new plan.
                      </span>
                    </div>
                  ) : null}

                  <div className="mt-5">
                    <button
                      className="btn btn-info mr-2"
                      type="submit"
                      disabled={
                        submitted ||
                        isLoadingMeta ||
                        (formik.values.plan_group === "other" &&
                          otherPlanCount >= 2)
                      }
                    >
                      <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                      {t("btn_submit")}
                    </button>
                    <button
                      className="btn ripple btn-secondary"
                      type="button"
                      disabled={submitted ? true : null}
                      onClick={() => navigate(-1)}
                    >
                      <i className="ace-icon fa fa-times bigger-110 mx-1"></i>
                      {t("btn_cancel")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default AddSubscriptionPlan;
